import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Calendar, Clock, User, MapPin, Play, Square, Bell, CheckCircle2, CheckCircle2Icon } from "lucide-react";
import toast from "react-hot-toast";
import { useSocket } from "../../../context/socket-context";
import { acceptTask, getScheduledTasks, rejectTask, transformTaskData } from "../../../services/scheduled-tasks-api";

// ==================== COMPONENT START ====================
const Schedule = () => {
  const navigate = useNavigate();
  const [latitude, setLatitude] = useState(31.4782);
  const [longitude, setLongitude] = useState(74.3140);
  const [address, setAddress] = useState("Loading location...");
  const [shiftActive, setShiftActive] = useState(false);
  const [shiftStartTime, setShiftStartTime] = useState(null);
  const [shiftLoading, setShiftLoading] = useState(false);
  const [ongoingTasks, setOngoingTasks] = useState([]);

  // ========== SCHEDULED TASKS STATE ==========
  const [scheduledTasksData, setScheduledTasksData] = useState([]); // API data
  const [availableTasks, setAvailableTasks] = useState([]); // Available tasks (pending offers)
  const [loadingTasks, setLoadingTasks] = useState(true); // Loading state
  const [tasksError, setTasksError] = useState(null); // Error state
  const [updatingStatus, setUpdatingStatus] = useState({}); // Tracking individual task updates

  // Group tasks by date for display
  const [appointments, setAppointments] = useState([]);


  const refreshCurrentLocation = async () => {
    if (!navigator.geolocation) {
      throw new Error("Geolocation not supported");
    }

    const pos = await new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: true,
        timeout: 20000,
        maximumAge: 0,
      });
    });

    const { latitude: lat, longitude: lng } = pos.coords;
    setLatitude(lat);
    setLongitude(lng);

    let resolvedAddress = `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`
      );
      const data = await response.json();
      if (data?.display_name) resolvedAddress = data.display_name;
    } catch {
      // Fall back to coordinates
    }

    setAddress(resolvedAddress);
    return { lat, lng, address: resolvedAddress };
  };

  // Get user data from localStorage
  const authUser = localStorage.getItem("authUser")
    ? JSON.parse(localStorage.getItem("authUser"))
    : null;
  const userId = authUser?._id;
  const token = localStorage.getItem("authToken");

  // Use Socket Context
  const {
    socket,
    isConnected,
    connectionStatus,
    scheduledTasks,
    setScheduledTasks,
    scheduledTaskUpdates
  } = useSocket();

  // ========== FETCH SCHEDULED TASKS FROM API ==========
  useEffect(() => {
    const fetchScheduledTasks = async () => {
      setLoadingTasks(true);
      setTasksError(null);

      try {
        // Call API to get scheduled tasks
        const result = await getScheduledTasks();

        if (result.success) {
          console.log('✅ Fetched scheduled tasks:', result);

          // Filter out live tasks if any are returned
          // const filteredTasks = result.tasks.filter(task => task.type !== 'live');

          setScheduledTasksData(result.tasks);
          setAvailableTasks(result.availableTasks || []);

          // Also update socket context state with filtered tasks
          setScheduledTasks(result.tasks);

          // Transform and group tasks by date using FILTERED tasks
          groupTasksByDate(result.tasks);
        } else {
          throw new Error(result.error || 'Failed to fetch scheduled tasks');
        }
      } catch (error) {
        console.error('❌ Error fetching scheduled tasks:', error);
        setTasksError(error.message);
        toast.error('Failed to load scheduled tasks');
      } finally {
        setLoadingTasks(false);
      }
    };

    fetchScheduledTasks();
  }, [setScheduledTasks]);

  // ========== LISTEN FOR REAL-TIME SOCKET UPDATES ==========
  useEffect(() => {
    if (scheduledTaskUpdates) {
      console.log('🔔 Scheduled task update received:', scheduledTaskUpdates);

      const { type, data } = scheduledTaskUpdates;

      switch (type) {
        case 'approved':
          toast.success('Your offer was approved! Task has been scheduled.');
          // Refresh tasks list
          refreshScheduledTasks();
          break;

        case 'rejected':
          toast.error('Your offer was rejected by the customer.');
          // Refresh tasks list
          refreshScheduledTasks();
          break;

        case 'scheduled':
          toast.success('New task scheduled!');
          // Refresh tasks list
          refreshScheduledTasks();
          break;

        case 'updated':
          toast.info('Task details have been updated.');
          // Refresh tasks list
          refreshScheduledTasks();
          break;

        case 'cancelled':
          toast.error('A scheduled task was cancelled.');
          // Refresh tasks list
          refreshScheduledTasks();
          break;

        default:
          break;
      }
    }
  }, [scheduledTaskUpdates]);

  // ========== HELPER: REFRESH SCHEDULED TASKS ==========
  const refreshScheduledTasks = async () => {
    try {
      const result = await getScheduledTasks();
      if (result.success) {
        setScheduledTasksData(result.tasks);
        setAvailableTasks(result.availableTasks || []);
        setScheduledTasks(result.tasks);
        groupTasksByDate(result.tasks);
      }
    } catch (error) {
      console.error('Error refreshing tasks:', error);
    }
  };

  // ========== HELPER: GROUP TASKS BY DATE ==========
  const groupTasksByDate = (tasks) => {
    if (!tasks || tasks.length === 0) {
      setAppointments([]);
      return;
    }

    // Group tasks by date
    const grouped = tasks.reduce((acc, task) => {
      const transformedTask = transformTaskData(task);
      const date = transformedTask.date;

      if (!acc[date]) {
        acc[date] = [];
      }

      // Convert to slot format for display
      acc[date].push({
        id: transformedTask.id,
        time: transformedTask.time,
        customer: transformedTask.customer.name,
        service: transformedTask.service.title,
        location: transformedTask.location.address,
        notes: transformedTask.service.notes || transformedTask.service.description?.substring(0, 50),
      });

      return acc;
    }, {});

    // Convert to array format
    const appointmentsArray = Object.keys(grouped).map(date => ({
      date,
      slots: grouped[date]
    }));

    setAppointments(appointmentsArray);
  };

  // ========== ACTIONS: ACCEPT/REJECT AVAILABLE TASKS ==========
  const handleAcceptTask = async (taskId, offerId, price) => {
    setUpdatingStatus(prev => ({ ...prev, [taskId]: true }));
    try {
      const result = await acceptTask(taskId, offerId, price);
      if (result.success) {
        toast.success('Task accepted successfully!');
        refreshScheduledTasks();
      } else {
        throw new Error(result.error || 'Failed to accept task');
      }
    } catch (error) {
      console.error('Error accepting task:', error);
      toast.error(error.message || 'Failed to accept task');
    } finally {
      setUpdatingStatus(prev => ({ ...prev, [taskId]: false }));
    }
  };

  const handleRejectTask = async (taskId, offerId) => {
    setUpdatingStatus(prev => ({ ...prev, [taskId]: true }));
    try {
      const result = await rejectTask(taskId, offerId, "Not available at this time", "time_conflict");
      if (result.success) {
        toast.success('Task rejected');
        refreshScheduledTasks();
      } else {
        throw new Error(result.error || 'Failed to reject task');
      }
    } catch (error) {
      console.error('Error rejecting task:', error);
      toast.error(error.message || 'Failed to reject task');
    } finally {
      setUpdatingStatus(prev => ({ ...prev, [taskId]: false }));
    }
  };




  // ==================== LOCATION ====================
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          const { latitude: lat, longitude: lng } = pos.coords;
          setLatitude(lat);
          setLongitude(lng);

          try {
            const response = await fetch(
              `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`
            );
            const data = await response.json();
            if (data?.display_name) setAddress(data.display_name);
          } catch {
            setAddress(`${lat.toFixed(4)}, ${lng.toFixed(4)}`);
          }
        },
        (err) => {
          console.error("Geolocation Error:", err);
          setAddress("Unable to get location");
          toast.error("Location access denied or timed out. Please enable location services.");
        }
      );
    }
  }, []);

  // ==================== SHIFT MANAGEMENT ====================
  const handleToggleShift = async () => {
    if (shiftActive) {
      await endShift();
    } else {
      await startShift();
    }
  };

  const startShift = async () => {
    try {
      if (navigator.permissions && navigator.permissions.query) {
        const permission = await navigator.permissions.query({
          name: "geolocation",
        });

        if (permission.state === "denied") {
          toast.error(
            "Location permission is denied. Please enable it in browser settings.",
            { duration: 5000 }
          );
          return;
        } else if (permission.state === "prompt") {
          try {
            const loc = await refreshCurrentLocation();
            await activateShift(loc);
          } catch (err) {
            console.error("Location access denied:", err);
            toast.error(err?.message || "Location access is required to start shift");
          }
          return;
        } else if (permission.state === "granted") {
          const loc = await refreshCurrentLocation();
          await activateShift(loc);
        }
      } else {
        try {
          const loc = await refreshCurrentLocation();
          await activateShift(loc);
        } catch (err) {
          console.error("Location access failed:", err);
          toast.error(err?.message || "Location access is required to start shift");
        }
      }
    } catch (error) {
      console.error("Permission check error:", error);
      try {
        const loc = await refreshCurrentLocation();
        await activateShift(loc);
      } catch (err) {
        toast.error(err?.message || "Location access is required to start shift");
      }
    }
  };

  const endShift = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/handyman/shift/end`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            user_id: userId,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok || data.success === false) {
        throw new Error(data.message || "Failed to end shift");
      }
      // Notify socket about going offline
      if (socket) {
        socket.emit('handyman:offline', { userId });
      }

      setShiftActive(false);
      setShiftStartTime(null);
      toast.success("Shift ended successfully");
    } catch (error) {
      console.error("Error ending shift:", error);
      toast.error(error.message || "Failed to end shift");
    }
  };

  const activateShift = async (loc) => {
    if (!userId) {
      toast.error("User not found. Please login again.");
      return;
    }

    setShiftLoading(true);

    try {
      const latToSend = loc?.lat ?? latitude;
      const lngToSend = loc?.lng ?? longitude;
      const addressToSend = loc?.address ?? address;

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/handyman/shift/start`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            lat: latToSend,
            lng: lngToSend,
            address: addressToSend,
            user_id: userId,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok || data.success === false) {
        throw new Error(data.message || "Failed to start shift");
      }

      // Notify socket about going online
      if (socket) {
        socket.emit('handyman:online', {
          userId,
          location: { lat: latToSend, lng: lngToSend, address: addressToSend }
        });
      }

      setShiftActive(true);
      setShiftStartTime(new Date());
      toast.success("Shift started! Listening for nearby tasks...");
    } catch (error) {
      console.error("Error starting shift:", error);
      toast.error(error.message || "Failed to start shift");
    } finally {
      setShiftLoading(false);
    }
  };



  const handleCardClick = (appointmentId) => {
    navigate(`/handyman/schedule/${appointmentId}`);
  };

  // Test function to manually create an ongoing task (for testing purposes)
  const createTestTask = () => {
    const testTaskId = `test-${Date.now()}`;
    const testTask = {
      serviceRequestId: testTaskId,
      customerName: "Test Customer",
      customerPhone: "+92 300 1234567",
      customerAvatar: "/static/media/default-avatar.png",
      basePrice: 5000,
      finalPrice: 5000,
      jobTitle: "Test Service Request",
      subcategory: "Plumbing, Repair",
      address: "Test Address, Lahore",
      description: "This is a test task to verify the workflow. Please test the Start Task and Complete Job buttons.",
      coordinates: [31.4782, 74.3140],
      isScheduled: false,
      scheduleDate: null,
      scheduleTime: null,
      status: "ongoing",
      workStatus: "ongoing",
      dealCompletedAt: new Date().toISOString(),
    };

    const existingSchedule = JSON.parse(localStorage.getItem('handymanSchedule') || '[]');
    existingSchedule.push(testTask);
    localStorage.setItem('handymanSchedule', JSON.stringify(existingSchedule));
    setOngoingTasks(existingSchedule);
    toast.success(`Test task created! ID: ${testTaskId}. Check the Ongoing Tasks section.`, {
      duration: 5000,
    });
  };

  // Connection status display
  const getConnectionStatusColor = () => {
    switch (connectionStatus) {
      case 'connected': return 'text-green-600 bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800 dark:text-green-400';
      case 'disconnected': return 'text-red-600 bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800 dark:text-red-400';
      case 'error': return 'text-orange-600 bg-orange-50 border-orange-200 dark:bg-orange-900/20 dark:border-orange-800 dark:text-orange-400';
      default: return 'text-gray-600 bg-gray-50 border-gray-200 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header - Matching Tasks page style */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 shadow-sm dark:bg-slate-900 dark:border-slate-700 rounded-lg">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Schedule</h1>
            <p className="text-gray-600 mt-1 flex items-center gap-2 dark:text-gray-300">
              <MapPin className="w-4 h-4 text-[#EFA938]" />
              {address}
            </p>
            <div className="flex items-center gap-4 mt-2">
              <div className={`px-3 py-1 rounded-full border text-xs font-medium flex items-center gap-2 ${getConnectionStatusColor()}`}>
                <Bell className="w-3 h-3" />
                {connectionStatus.toUpperCase()}
              </div>
              {appointments.length > 0 && (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {appointments.reduce((total, day) => total + day.slots.length, 0)} appointment{appointments.reduce((total, day) => total + day.slots.length, 0) !== 1 ? 's' : ''} scheduled
                </p>
              )}
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Test button - remove in production */}
            <button
              onClick={createTestTask}
              className="px-4 py-2 rounded-lg border border-blue-300 text-blue-600 font-semibold hover:bg-blue-50 transition-colors text-sm"
              title="Create a test task for testing the workflow"
            >
              🧪 Test Task
            </button>
            <button
              onClick={handleToggleShift}
              disabled={shiftLoading || !isConnected}
              className={`px-6 py-2 rounded-lg text-white font-semibold transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${shiftActive
                ? "bg-red-500 hover:bg-red-600"
                : "bg-[#EFA938] hover:bg-[#d78c1f]"
                }`}
            >
              {shiftLoading ? (
                <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
              ) : shiftActive ? (
                <>
                  <Square className="w-4 h-4" />
                  End Shift
                </>
              ) : (
                <>
                  <Play className="w-4 h-4" />
                  Start Shift
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Connection Status Info */}
      {!isConnected && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 dark:bg-yellow-900/20 dark:border-yellow-800">
          <div className="flex items-center gap-3">
            <Bell className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
            <div>
              <p className="text-sm font-medium text-yellow-800 dark:text-yellow-300">
                Socket Disconnected
              </p>
              <p className="text-sm text-yellow-700 dark:text-yellow-400 mt-1">
                Real-time updates are not available. Please check your connection.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden dark:bg-slate-800 dark:border-slate-700">
        <header className="px-6 py-4 border-b border-gray-100 dark:border-slate-700 flex items-center gap-3">
          <Calendar className="h-5 w-5 text-[#EFA938]" />
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Upcoming Appointments</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">All confirmed jobs for the next 7 days</p>
          </div>
        </header>

        <div className="divide-y divide-gray-100 dark:divide-slate-700">
          {/* Available Tasks Section */}
          {availableTasks.length > 0 && (
            <section className="px-6 py-5 bg-amber-50/30 dark:bg-amber-900/10">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-amber-600 dark:text-amber-400 mb-3 flex items-center justify-between">
                Available Tasks
                <span className="bg-amber-200 text-amber-800 dark:bg-amber-900 dark:text-amber-200 px-2 py-0.5 rounded-full text-xs">
                  Action Required
                </span>
              </h3>
              <div className="space-y-4">
                {availableTasks.map((task) => {
                  const transformed = transformTaskData(task);
                  return (
                    <article
                      key={transformed.id}
                      className="group relative rounded-2xl border border-amber-200 bg-white p-5 hover:shadow-xl hover:border-amber-400 transition-all duration-300 dark:border-amber-700/50 dark:bg-slate-800/50"
                    >
                      {/* Premium Accent Line */}
                      <div className="absolute left-0 top-0 bottom-0 w-1 bg-amber-400 rounded-l-2xl opacity-0 group-hover:opacity-100 transition-opacity"></div>

                      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                        <div className="flex-1 space-y-3">
                          <div className="flex flex-wrap items-center gap-4">
                            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-100 text-amber-700 text-xs font-bold dark:bg-amber-900/30 dark:text-amber-400">
                              <Bell className="h-3.5 w-3.5 animate-pulse" />
                              NEW REQUEST
                            </span>
                            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                              <Calendar className="h-4 w-4" />
                              <span>{transformed.date}</span>
                              <span className="mx-1">•</span>
                              <Clock className="h-4 w-4" />
                              <span>{transformed.time}</span>
                            </div>
                          </div>

                          <div className="space-y-1">
                            <h4 className="text-xl font-bold text-gray-900 dark:text-white group-hover:text-amber-600 transition-colors">
                              {transformed.service.title}
                            </h4>
                            <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                              <MapPin className="h-4 w-4 text-amber-500" />
                              <span className="text-sm font-medium">{transformed.location.address}</span>
                            </div>
                          </div>

                          <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 max-w-2xl">
                            {transformed.service.description || "No description provided for this request."}
                          </p>

                          <div className="flex flex-wrap items-center gap-4 pt-1">
                            <div className="bg-gray-50 dark:bg-slate-700/50 px-3 py-1.5 rounded-lg border border-gray-100 dark:border-slate-600">
                              <span className="text-xs text-gray-500 block uppercase font-bold tracking-wider">Estimated Budget</span>
                              <span className="text-lg font-black text-gray-900 dark:text-white">PKR {transformed.workDetails.basePrice.toLocaleString()}</span>
                            </div>
                            <div className="bg-gray-50 dark:bg-slate-700/50 px-3 py-1.5 rounded-lg border border-gray-100 dark:border-slate-600">
                              <span className="text-xs text-gray-500 block uppercase font-bold tracking-wider">Duration</span>
                              <span className="text-sm font-bold text-gray-800 dark:text-gray-200">{transformed.workDetails.estimatedTime}</span>
                            </div>
                          </div>
                        </div>

                        <div className="flex flex-row lg:flex-col gap-3 min-w-[160px]">
                          <button
                            onClick={() => handleAcceptTask(transformed.id, task.myOffer?.id || transformed.id, transformed.workDetails.basePrice)}
                            disabled={updatingStatus[transformed.id]}
                            className="flex-1 lg:w-full px-6 py-3 rounded-xl bg-[#EFA938] text-white font-bold hover:bg-[#d78c1f] transition-all transform hover:scale-[1.02] active:scale-[0.98] shadow-md hover:shadow-lg disabled:opacity-50 flex items-center justify-center gap-2"
                          >
                            {updatingStatus[transformed.id] ? (
                              <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                            ) : (
                              <>
                                <CheckCircle2Icon className="h-5 w-5" />
                                Accept
                              </>
                            )}
                          </button>
                          <div className="flex gap-2 flex-1 lg:w-full">
                            <button
                              onClick={() => handleRejectTask(transformed.id, task.myOffer?.id || transformed.id)}
                              disabled={updatingStatus[transformed.id]}
                              className="flex-1 px-4 py-3 rounded-xl border-2 border-red-100 text-red-600 font-bold hover:bg-red-50 hover:border-red-200 transition-all text-sm disabled:opacity-50"
                            >
                              Reject
                            </button>
                            <button
                              onClick={() => handleCardClick(transformed.id)}
                              className="flex-1 px-4 py-3 rounded-xl border-2 border-gray-100 text-gray-700 font-bold hover:bg-gray-50 hover:border-gray-200 transition-all text-sm dark:border-slate-700 dark:text-gray-300"
                            >
                              Details
                            </button>
                          </div>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            </section>
          )}

          {/* Ongoing/Current Tasks Section */}
          {ongoingTasks.length > 0 && (
            <section className="px-6 py-5">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-orange-600 dark:text-orange-400 mb-3">
                Ongoing Tasks
              </h3>
              <div className="space-y-4">
                {ongoingTasks.map((task) => (
                  <article
                    key={task.serviceRequestId}
                    onClick={() => handleCardClick(task.serviceRequestId)}
                    className="rounded-2xl border-2 border-orange-300 bg-orange-50/60 px-4 py-4 md:px-6 md:py-5 hover:bg-orange-50 transition-colors cursor-pointer dark:border-orange-600 dark:bg-orange-900/20 dark:hover:bg-orange-900/30"
                  >
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                      <div className="space-y-2 flex-1">
                        <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600 dark:text-gray-300">
                          <span className="inline-flex items-center gap-1.5 font-medium text-orange-700 dark:text-orange-400">
                            <Clock className="h-4 w-4 text-[#EFA938]" />
                            {task.isScheduled && task.scheduleTime ? task.scheduleTime : 'Now'}
                          </span>
                          <span className="inline-flex items-center gap-1.5">
                            <User className="h-4 w-4 text-[#EFA938]" />
                            {task.customerName}
                          </span>
                          <span className="inline-flex items-center gap-1.5">
                            <MapPin className="h-4 w-4 text-[#EFA938]" />
                            {task.address}
                          </span>
                          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-orange-200 text-orange-800 dark:bg-orange-900 dark:text-orange-200 text-xs font-semibold">
                            {task.status === 'ongoing' ? 'Ongoing' : 'Current'}
                          </span>
                        </div>
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-white">{task.jobTitle}</h4>
                        <p className="text-sm text-gray-600 dark:text-gray-300">{task.description?.substring(0, 100)}...</p>
                      </div>
                      <div className="flex flex-col sm:flex-row gap-2" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCardClick(task.serviceRequestId);
                          }}
                          className="px-4 py-2 rounded-lg border border-orange-300 text-orange-700 text-sm font-medium hover:bg-orange-100 transition-colors dark:border-orange-600 dark:text-orange-300 dark:hover:bg-orange-900/50"
                        >
                          View Details
                        </button>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </section>
          )}

          {/* Regular Appointments Section */}
          {appointments.map((day) => (
            <section key={day.date} className="px-6 py-5">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-3">
                {day.date}
              </h3>
              <div className="space-y-4">
                {day.slots.map((slot, idx) => (
                  <article
                    key={`${day.date}-${slot.time}-${idx}`}
                    onClick={() => handleCardClick(slot.id)}
                    className="rounded-2xl border border-gray-100 bg-gray-50/60 px-4 py-4 md:px-6 md:py-5 hover:bg-gray-50 transition-colors cursor-pointer dark:border-slate-700 dark:bg-slate-700/60 dark:hover:bg-slate-700"
                  >
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                      <div className="space-y-2 flex-1">
                        <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500 dark:text-gray-400">
                          <span className="inline-flex items-center gap-1.5 font-medium text-gray-900 dark:text-white">
                            <Clock className="h-4 w-4 text-[#EFA938]" />
                            {slot.time}
                          </span>
                          <span className="inline-flex items-center gap-1.5">
                            <User className="h-4 w-4 text-[#EFA938]" />
                            {slot.customer}
                          </span>
                          <span className="inline-flex items-center gap-1.5">
                            <MapPin className="h-4 w-4 text-[#EFA938]" />
                            {slot.location}
                          </span>
                        </div>
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-white">{slot.service}</h4>
                        <p className="text-sm text-gray-600 dark:text-gray-300">{slot.notes}</p>
                      </div>
                      <div className="flex flex-col sm:flex-row gap-2" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCardClick(slot.id);
                          }}
                          className="px-4 py-2 rounded-lg border border-gray-200 text-gray-600 text-sm font-medium hover:bg-white transition-colors dark:border-slate-600 dark:text-gray-300 dark:hover:bg-slate-600"
                        >
                          View Details
                        </button>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </section>
          ))}
        </div>

        {/* Empty State */}
        {appointments.length === 0 && availableTasks.length === 0 && (
          <div className="px-6 py-12 text-center">
            <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No Appointments</h3>
            <p className="text-gray-500 dark:text-gray-400">
              You don't have any scheduled appointments or available tasks.
            </p>
          </div>
        )}
      </div>

      {/* Shift Status Card */}
      {shiftActive && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 dark:bg-green-900/20 dark:border-green-800">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-2 w-2 bg-green-500 rounded-full animate-pulse"></div>
              <div>
                <p className="text-sm font-medium text-green-800 dark:text-green-300">
                  Shift Active
                </p>
                <p className="text-sm text-green-700 dark:text-green-400">
                  {shiftStartTime && `Started at ${shiftStartTime.toLocaleTimeString()}`}
                </p>
              </div>
            </div>
            <div className={`px-3 py-1 rounded-full text-xs font-medium ${isConnected
              ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
              : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
              }`}>
              Socket: {isConnected ? 'Connected' : 'Disconnected'}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Schedule;